import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import CaseStudyCard from './cards/CaseStudyCard';
import { device } from '../../utils/styled-component-mixins';
import { withWidgetSpacingWrapper } from '../shared/WidgetSpacingWrapper';
import WidgetIdWrapper from '../shared/WidgetIdWrapper';
import { withLocalization } from '../shared/Context/LocalizationContext';
import { useLocalization } from '../custom-hooks/useLocalization';
import { compose } from '../utils/compose';

const FeaturedCaseStudyWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media ${device.tablet} {
    display: grid;
    grid-template-columns: repeat(${(props) => props.numberOfCards}, minmax(0, 420px));
    row-gap: 1.6rem;
    column-gap: 1.6rem;
    justify-content: center;
  }

  &.preload * {
    animation: none !important;
    transition: none !important;
  }

  & > a {
    flex: 0 0 100%;
    overflow: hidden;
    padding: 3.2rem 2.4rem;
    position: relative;

    @media ${device.tablet} {
      padding: 3.2rem 4rem;

      &:last-child {
        margin-right: 0;
      }

      &:nth-child(n + 4) {
        margin-top: 1.6rem;
      }
    }
  }
`;

const FeaturedCaseStudyCtaWrapper = styled.div`
  .cta-button {
    position: relative;
    left: 91.25%;
    margin-top: 1rem;
  }
`;

const FeaturedCaseStudyComponent = (props) => {
  const [isFirstRender, setIsFirstRender] = useState(true);
  const featuredCardWidgetRef = useRef(null);
  const childTitleHeightsCount = useRef(0);
  const tallestTitleHeight = useRef(0);
  const { resourceStrings } = useLocalization();
  const linkText = resourceStrings['bdO.FeaturedCaseStudy.LinkText'];

  const setInitialContentPosition = (card, titleHeightDifference) => {
    const elementToAnimate = card.querySelector('.content-wrapper');
    const content = card.children[0].children[0].children[0].children[0].children[2];
    let contentHeight =
      card.children[0].children[0].children[0].children[0].children[2].clientHeight;
    const contentStyle = getComputedStyle(content);

    contentHeight += parseFloat(contentStyle.marginTop) + parseFloat(contentStyle.marginBottom);
    const bottomPositionInRem = (contentHeight - titleHeightDifference) / 10;

    elementToAnimate.style.setProperty('--bottom-position', `-${bottomPositionInRem}rem`);
  };

  const setInitialContentPositions = () => {
    const cards = featuredCardWidgetRef.current.querySelectorAll('a');

    cards.forEach((card) => {
      const title = card.querySelector('h2');
      const titleStyles = getComputedStyle(title);
      const titleMarginBottom = parseInt(titleStyles.marginBottom, 10);
      const titleHeight = title.getBoundingClientRect().height + titleMarginBottom;
      const titleHeightDifference = tallestTitleHeight.current - titleHeight;
      setInitialContentPosition(card, titleHeightDifference);
    });
  };

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }

    if (
      document.readyState === 'complete' &&
      childTitleHeightsCount.current === props.items.length
    ) {
      setInitialContentPositions();
    } else {
      window.addEventListener('load', setInitialContentPositions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTitleHeightCallBack = (height) => {
    childTitleHeightsCount.current += 1;
    if (height > tallestTitleHeight.current) {
      tallestTitleHeight.current = height;
    }

    if (childTitleHeightsCount.current === props.items.length) {
      setInitialContentPositions();
    }
  };

  return (
    <WidgetIdWrapper widgetID={props.properties.widgetID}>
      {!isFirstRender && (
        <FeaturedCaseStudyWrapper
          ref={featuredCardWidgetRef}
          className={`${isFirstRender ? 'preload' : ''}`}
          numberOfCards={props.items.length}
        >
          {props.items.map((item, index) => (
            <CaseStudyCard
              getTitleHeightCallBack={getTitleHeightCallBack}
              key={`${item.pageTitle} ${index}`}
              type="case study"
              linkText={linkText}
              {...item}
            />
          ))}
        </FeaturedCaseStudyWrapper>
      )}
      {props.properties.exploreMoreLinkUrl && (
        <FeaturedCaseStudyCtaWrapper>
          <div class="btn-wrapper cta-wrapper">
            <div class="cta-button">
              <a
                href={props.properties.exploreMoreLinkUrl}
                class="non-default-link btn btn-tertiary btn-tertiary-undefined btn-light-theme btn-align-undefined"
              >
                <span>{props.properties.exploreMoreLinkText}</span>
                <span class="material-icons-round">chevron_right</span>
              </a>
            </div>
          </div>
        </FeaturedCaseStudyCtaWrapper>
      )}
    </WidgetIdWrapper>
  );
};

export default compose(withLocalization, withWidgetSpacingWrapper)(FeaturedCaseStudyComponent);
